import type { ReactElement } from 'react';

import type { EmailPlansData, EmailPlansHandlers } from '@ctct/application-layer';
import type { ContactsTierSelectProps } from '@ctct/payflow-ui';
import { CardSelect, Heading, MWebCardSelect } from '@ctct/payflow-ui';
import type { EmailPlansHooks } from '@ctct/state-client-layer';

import type { EmailPlansWidgetProps } from './useEmailPlansWidget';

interface Props {
  data: EmailPlansData;
  handlers: EmailPlansHandlers;
  hooks: EmailPlansHooks;
  externalHooks: EmailPlansWidgetProps['externalHooks'];
  contactsTierSelectWidget?: ReactElement<ContactsTierSelectProps>;
}

export const EmailPlansWidgetContent = ({ data, handlers, hooks, externalHooks, contactsTierSelectWidget }: Props) => {
  const tierMin = externalHooks.useContactsTier();
  const prepay = externalHooks.usePrepay();
  const smsBundleTierForStore = externalHooks.useSmsBundleTier();

  const disabled = data.getIsDisabled();
  const emailPlans = data.getContent(
    parseInt(tierMin, 10),
    prepay,
    data.getIsSmsBundle() ? smsBundleTierForStore : null
  );
  const selectedPlanId = hooks.useSelectedEmailPlanId() || '';

  const isMWebVariantEnabled = data.getIsMWebVariantEnabled();
  const isValueFocusedMWebPlanSelectionEnabled = data.getIsValueFocusedMWebPlanSelectionEnabled();

  if (isMWebVariantEnabled) {
    const popularBannerText = isValueFocusedMWebPlanSelectionEnabled ? data.getPopularBannerText() : undefined;

    return (
      <MWebCardSelect
        disabled={disabled}
        options={emailPlans.map((plan) => ({
          ...plan,
          bottomDescription:
            typeof plan.bottomDescription === 'string' ? (
              plan.bottomDescription
            ) : (
              <div>
                <Heading level="h6-small">{plan.bottomDescription.heading}</Heading>
                <p style={{ color: '#0f141c', marginBottom: 12 }}>{plan.bottomDescription.description}</p>
              </div>
            ),
        }))}
        onOptionSelect={handlers.onSelectPlanClick}
        value={String(selectedPlanId)}
        clickButtonToSelect
        type="plan"
        contactsTierSelectWidget={contactsTierSelectWidget}
        alignContent={isValueFocusedMWebPlanSelectionEnabled ? 'left' : 'center'}
        showBulletTicks={isValueFocusedMWebPlanSelectionEnabled}
        oldValuePlacement={isValueFocusedMWebPlanSelectionEnabled ? 'after' : 'before'}
        popularBannerText={popularBannerText}
        onRemoveSms={handlers.onRemoveSmsClick}
      />
    );
  }

  return (
    <CardSelect
      disabled={disabled}
      buttonFullWidth={data.getIsSmsBundle()}
      options={emailPlans.map((plan) => ({
        ...plan,
        bottomDescription:
          typeof plan.bottomDescription === 'string' ? (
            plan.bottomDescription
          ) : (
            <div>
              <Heading level="h6-small">{plan.bottomDescription.heading}</Heading>
              <p style={{ color: '#0f141c', marginBottom: 12 }}>{plan.bottomDescription.description}</p>
            </div>
          ),
      }))}
      onOptionSelect={handlers.onSelectPlanClick}
      value={String(selectedPlanId)}
      clickButtonToSelect
      type="plan"
      onRemoveSms={handlers.onRemoveSmsClick}
    />
  );
};
